<template>
  <router-view />
</template>

<script>
import { mapState } from "vuex";
import { AuthStatus } from "./vuex/modules/User.store";

import "@/sass/dep.scss";
import "@/sass/animations.scss";
import moment from "moment";
import { mapActions } from "vuex/dist/vuex.common.js";

let timer;

export default {
  name: "app",
  watch: {
    authStatus(newStatus, oldStatus) {
      if (oldStatus != newStatus) {
        if (newStatus == AuthStatus.authenticated) {
          this.injectZohoChat();
        } else {
          this.hideZohoChat();
        }

        const isOnLoginAlready = this.$route.path == "/login";
        if (newStatus == AuthStatus.unauthenticated && !isOnLoginAlready) {
          this.$router.replace("/login");
          if (timer) {
            clearTimeout(timer);
          }
        }
      }
    },
    expiresIn() {
      this.setSessionExpiration();
    },
    relative(data) {
      const isOnWizardsPath = [
        "/payments/wizard",
        "/payments/wizard/invoice-info",
        "/payments/wizard/shipping-info",
        "/payments/wizard/association-info",
      ].includes(this.$route.path);
      if (
        !data ||
        isOnWizardsPath ||
        this.authStatus == AuthStatus.unauthenticated
      ) {
        return;
      }

      const { signUpStatus } = data;

      switch (signUpStatus) {
        case "Pending to accept agreement":
        case "Pending to give general info":
          this.$router.replace("/payments/wizard");
          break;
        case "Pending to give invoice info":
          this.$router.replace("/payments/wizard/invoice-info");
          break;
        case "Pending to give shipment info":
          this.$router.replace("/payments/wizard/shipping-info");
          break;
        case "Pending to associate children":
          this.$router.replace("/payments/wizard/association-info");
          break;
      }
    },
    $route(val) {
      this.validateZohoScriptState(val);
    },
  },
  mounted() {
    this.setSessionExpiration();
  },
  methods: {
    ...mapActions("User", ["logout"]),
    async injectZohoChat() {
      const ZOHO_KEYS = {
        RELATIVE:
          "77cf77eea6f7d1ec7a461c37e277c3f724c201e8bff3db20e208f397975ec500",
        TI: "siqf1abf95e8ab50e4b03e7f6827aeb5e17",
      };

      try {
        const { data: newUser } = await this.$depAPI.util.getUserOptions();

        // Determine which key to use based on user type
        const key = this.$isRelative
          ? ZOHO_KEYS.RELATIVE
          : newUser && newUser.isTI
          ? ZOHO_KEYS.TI
          : "";

        if (!key) return;

        const existingScript = document.getElementById("custom_script_zoho");
        if (existingScript) {
          existingScript.remove();
        }

        const zohoScript = document.createElement("script");
        zohoScript.id = "custom_script_zoho";
        zohoScript.text = `
          var $zoho = $zoho || {};
          $zoho.salesiq = $zoho.salesiq || {
            widgetcode: '${key}',
            values: {},
            ready: function() {
                $zoho.salesiq.visitor.name('${newUser.fullName}');
                $zoho.salesiq.visitor.email('${newUser.email}');
            }
          };


        `;

        document.body.prepend(zohoScript);
      } catch {
        // code
      }
    },
    async validateZohoScriptState(route) {
      const INVALID_PATHS = ["/Knotion-bank", "/planner", "/dashboard"];

      const isInInvalidPath = INVALID_PATHS.some(
        (path) => route.path === path || route.path.startsWith(`${path}/`)
      );

      try {
        const { data: newUser } = await this.$depAPI.util.getUserOptions();

        const shouldShowChat =
          this.$isRelative ||
          (newUser && newUser.isTI && !isInInvalidPath && this.isWorkTime());

        shouldShowChat ? this.showZohoChat() : this.hideZohoChat();
      } catch (error) {
        this.hideZohoChat();
      }
    },
    hideZohoChat() {
      const salesiqScript = document.getElementById("zsiq_float");
      if (salesiqScript) {
        salesiqScript.style.setProperty("display", "none", "important");
      }
    },
    showZohoChat() {
      const salesiqScript = document.getElementById("zsiq_float");
      if (salesiqScript) {
        salesiqScript.style.setProperty("display", "block", "important");
      }
    },
    isWorkTime() {
      const workDays = [1, 2, 3, 4, 5];
      const day = new Date().getDay();

      const now = new Date().toLocaleString("en-US", {
        timeZone: "America/Mexico_City",
      });

      const nowDate = new Date(now);
      const end = new Date(now);
      end.setHours(17, 0, 0);

      // Set timeout to hide chat at 5:00 PM
      const timeUntil5PM = end.getTime() - nowDate.getTime();
      if (timeUntil5PM > 0) {
        setTimeout(() => {
          this.hideZohoChat();
        }, timeUntil5PM);
      }

      return workDays.includes(day);
    },
    setSessionExpiration() {
      let _this = this;
      if (this.expiresIn) {
        const secondsToExpire = this.expiresIn.diff(moment(), "seconds");
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          _this.logout();
        }, secondsToExpire * 1000);
      }
    },
  },
  computed: {
    ...mapState("User", ["authStatus", "relative", "expiresIn"]),
  },
};
</script>

<style lang="scss">
@import "./assets/fonts/glyphicons/glyphicons.min.css";
@import "./assets/fonts/ionicons/ionicons.min.css";
@import "./assets/fonts/web-icons/web-icons.css";
@import "./sass/vendor/remark/bootstrap.scss";
@import "./sass/vendor/remark/bootstrap-extend.scss";
@import "./sass/vendor/remark/site.scss";
@import "./sass/vendor/select2/core.scss";

/** Vue Modal Overrides */
.vm--container {
  z-index: 1508 !important;
}

#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}

.kno-loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7) !important;
  z-index: 999999;
  padding-top: 65px;
  opacity: 1 !important;
}

.kno-loader-hidden {
  display: none !important;
}

.kno-loader-container__text {
  position: absolute;
  top: calc(50% - 10px);
  left: calc(50% - 30px);
  color: white;
  text-align: center;
  width: 80px;
}

.kno-loader-container__spinner:before {
  content: "";
  box-sizing: border-box;
  position: absolute;
  top: calc(50% - 50px);
  left: calc(50% - 50px);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #579adc;
  border-bottom-color: #579adc;
  animation: spinner_back 3s ease infinite;
}

.kno-loader-container__spinner:after {
  content: "";
  box-sizing: border-box;
  position: absolute;
  top: calc(50% - 40px);
  left: calc(50% - 40px);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #07d;
  border-bottom-color: #07d;
  animation: spinner 1.8s ease infinite;
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

@keyframes spinner_back {
  to {
    transform: rotate(-360deg);
  }
}

@media (min-width: 620px) {
  .kno-loader-container__spinner:before {
    width: 160px;
    height: 160px;
    top: calc(50% - 80px);
    left: calc(50% - 80px);
  }

  .kno-loader-container__spinner:after {
    width: 140px;
    height: 140px;
    top: calc(50% - 70px);
    left: calc(50% - 70px);
  }

  .kno-loader-container__text {
    font-size: 18px;
    left: calc(50% - 40px);
  }
}
</style>
